import { useEffect } from 'react';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { TranslatedInput } from '../common/TranslatedInput';
import type { LanguageCode, ServiceTypeTranslation } from '../../types/i18n';
import type { ServiceType } from '../../types/business';

interface ServiceTypeFormProps {
  initialData?: ServiceType | null;
  singleName: string;
  onSingleNameChange: (val: string) => void;
  translations: Partial<Record<LanguageCode, string>>;
  onTranslationsChange: (val: Partial<Record<LanguageCode, string>>) => void;
  supportedLanguages: LanguageCode[];
  defaultLanguage: LanguageCode;
}

export function ServiceTypeForm({
  initialData,
  singleName,
  onSingleNameChange,
  translations,
  onTranslationsChange,
  supportedLanguages,
  defaultLanguage,
}: ServiceTypeFormProps) {
  const { t } = useTranslation('owner');

  // Initialize translations from initialData when the edited item changes.
  // Intentionally keyed only on initialData?.id so callbacks do not cause re-runs.
  const initialDataId = initialData?.id;
  useEffect(() => {
    if (!initialData) return;

    if (initialData.translations && initialData.translations.length > 0) {
      const map: Partial<Record<LanguageCode, string>> = {};
      initialData.translations.forEach((tr) => {
        map[tr.languageCode] = tr.name;
      });
      onTranslationsChange(map);
    } else if (supportedLanguages.length > 1 && initialData.name) {
      // Legacy data: item was created in single-language mode but tenant now supports
      // multiple languages. Seed the default-language tab with the existing name
      // so the user sees the original value instead of empty tabs.
      onTranslationsChange({ [defaultLanguage]: initialData.name });
    } else if (initialData.name) {
      onSingleNameChange(initialData.name);
    }
  }, [initialDataId]);

  if (supportedLanguages.length <= 1) {
    return (
      <Input
        value={singleName}
        onChange={(e) => onSingleNameChange(e.target.value)}
        placeholder={t('settings.serviceTypes.namePlaceholder')}
        style={{ borderRadius: 12, height: 40 }}
        autoFocus
      />
    );
  }

  return (
    <TranslatedInput
      value={translations}
      onChange={onTranslationsChange}
      supportedLanguages={supportedLanguages}
      defaultLanguage={defaultLanguage}
      required
      placeholder={t('settings.serviceTypes.namePlaceholder')}
      label={t('settings.serviceTypes.nameLabel')}
      layout="stacked"
    />
  );
}

export function buildServiceTypePayload(
  supportedLanguages: LanguageCode[],
  singleName: string,
  translations: Partial<Record<LanguageCode, string>>,
  defaultLanguage: LanguageCode,
): { name: string; translations?: ServiceTypeTranslation[] } | null {
  if (supportedLanguages.length <= 1) {
    const name = singleName.trim();
    if (name.length < 2) return null;
    return { name };
  }

  const defaultName = translations[defaultLanguage]?.trim() ?? '';
  if (defaultName.length < 2) return null;

  const translationsArr: ServiceTypeTranslation[] = supportedLanguages
    .filter((lang) => translations[lang]?.trim())
    .map((lang) => ({
      languageCode: lang,
      name: translations[lang]!.trim(),
      description: null,
    }));

  return { name: defaultName, translations: translationsArr };
}
